import React, { useState, useEffect } from 'react';
import { Tag } from 'primereact/tag';
import { Avatar } from 'primereact/avatar';
import { ProgressSpinner } from 'primereact/progressspinner';
import ProteinFeatureChips from '../../components/ProteinFeatureChips/ProteinFeatureChips';

const ALLOWED_DATABASES = [
    'Pfam',
    'ChEMBL',
    'IntAct',
    'InterPro',
    'AlphaFoldDB',
    'PhosphoSitePlus'
];


const ECO_LABELS = {
    'ECO:0000269': 'Experimental',
    'ECO:0000250': 'By similarity',
    'ECO:0000305': 'Curator inference',
    'ECO:0000255': 'Sequence analysis',
    'ECO:0000256': 'Automatic',
    'ECO:0000312': 'Imported',
    'ECO:0000244': 'Combinatorial',
    'ECO:0000303': 'Non-traceable',
    'ECO:0000501': 'Electronic',
    'ECO:0007744': 'Combinatorial evidence',
    'ECO:0007829': 'Derived',
    'ECO:0007297': 'Derived'
};

const LOCATION_HIGHLIGHTS = {
    'Cytoplasm': 'cytoplasm',
    'Cytosol': 'cytosol',
    'Nucleus': 'nucleus',
    'Nucleoplasm': 'nucleus',
    'Mitochondrion': 'mitochondrion',
    'Cell membrane': 'plasma-membrane',
    'Plasma membrane': 'plasma-membrane',
    'Endoplasmic reticulum': 'er',
    'Golgi apparatus': 'golgi',
    'Lysosome': 'lysosome',
    'Peroxisome': 'peroxisome',
    'Centrosome': 'centrosome',
    'Cytoplasmic vesicle': 'vesicle',
    'Secretory vesicle': 'vesicle',
    'Cell projection': 'cell-projection',
    'Cilium': 'cell-projection',
    'Flagellum': 'cell-projection',
    'Acrosome': 'vesicle',
    'Extracellular': 'extracellular',
    'Secreted': 'extracellular'
};

const OverviewSection = ({ target, data, overviewLoadData, setActiveTabIndex }) => {

    const [severity, setSeverity] = useState("success");
    const [family, setFamily] = useState("");
    const [name, setName] = useState("");
    const [status, setStatus] = useState("Reviewed");
    const [seqLength, setSeqLength] = useState(0);
    const [subcellularLocations, setSubcellularLocations] = useState([]);
    const [crossReferences, setCrossReferences] = useState({});
    const [highlightedParts, setHighlightedParts] = useState([]);

    useEffect(() => {
        const container = document.getElementById('fv1');
        if (container) {
            container.innerHTML = '';
        }

        const sev = data?.status === "reviewed" ? "success" : "warning";
        setSeverity(sev);
        setFamily(data?.family);
        setName(data?.name);
        setStatus(data?.status);
        setSeqLength(data?.seqLength);

        if (data?.subcellularLocation) {
            const locations = parseSubcellularLocations(
                data.subcellularLocation,
                data.subcellularLocationEvidence
            );
            setSubcellularLocations(locations);

            const parts = new Set();
            locations.forEach(loc => {
                // Splitta "Cell projection, cilium, flagellum" su virgola
                loc.location.split(',').forEach(part => {
                    const trimmed = part.trim();
                    // Controllo match case-insensitive
                    Object.keys(LOCATION_HIGHLIGHTS).forEach(key => {
                        if (trimmed.toLowerCase().includes(key.toLowerCase())) {
                            parts.add(LOCATION_HIGHLIGHTS[key]);
                        }
                    });
                });
            });
            setHighlightedParts([...parts]);
        }

        if (data?.crossReferences) {
            const filtered = ALLOWED_DATABASES.reduce((acc, dbName) => {
                if (data.crossReferences[dbName]) {
                    acc[dbName] = data.crossReferences[dbName];
                }
                return acc;
            }, {});
            setCrossReferences(filtered);
        }
    }, [target, data]);

    const parseSubcellularLocations = (locationStr, evidenceStr) => {
        if (!locationStr) return [];
        const locations = locationStr.split('|').map(l => l.trim());
        const evidences = evidenceStr ? evidenceStr.split('|').map(e => e.trim()) : [];

        return locations.map((loc, idx) => {
            const isoformMatch = loc.match(/^\[([^\]]+)\]:\s*(.+)$/);
            const isoform = isoformMatch ? isoformMatch[1] : null;
            const location = isoformMatch ? isoformMatch[2] : loc;

            const rawEvidence = evidences[idx] || '';
            const evidenceBadges = parseEvidenceBadges(rawEvidence);

            return {
                isoform,
                location,
                evidenceBadges,
                rawEvidence
            };
        });
    };

    const parseEvidenceBadges = (evidenceStr) => {
        if (!evidenceStr) return [];
        const badges = [];
        const ecoMatches = evidenceStr.match(/ECO:\d+/g) || [];
        const uniqueEcos = [...new Set(ecoMatches)];

        uniqueEcos.forEach(eco => {
            const label = ECO_LABELS[eco] || 'Evidence';
            badges.push({ label, eco });
        });

        return badges;
    };

    const renderCrossRefEntry = (dbName, entry, idx) => {
        const primaryId = entry.ids?.[0] || '';
        const url = entry.url;
        return (
            <a
                key={`${dbName}-${idx}`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    color: '#0d6efd',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    marginRight: '0.75rem',
                    whiteSpace: 'nowrap',
                    display: 'inline-block'
                }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
            >
                {primaryId}
            </a>
        );
    };

    return (
        <div className="col-12">
            {overviewLoadData && (
                <div className="row mb-4">
                    <ProgressSpinner
                        style={{ width: '50px', height: '50px' }}
                        strokeWidth="8"
                        fill="var(--surface-ground)"
                        animationDuration=".5s"
                    />
                </div>
            )}
            {!overviewLoadData && (
                <div className="row">
                    <h2 className="display-6 fw-bold mb-4 protein-line">
                        {name}
                        <sup> <Tag value={status} severity={severity} rounded /> </sup>
                    </h2>
                    <div className="row col-12">
                        <div className="fs-1 text-justify mb-2">
                            <Avatar label="F" size="xlarge" /> {family}
                        </div>
                        <p className="fs-1 text-justify"> {seqLength} Amino acids</p>
                    </div>
                    <div className="row mb-5 col-12">
                        <ProteinFeatureChips data={data} setActiveTabIndex={setActiveTabIndex} />
                    </div>

                    {/* ========== SUBCELLULAR LOCATIONS ========== */}
                    {subcellularLocations.length > 0 && (
                        <div className="row mb-5 col-12">
                            <h3 className="fw-bold mb-3">Subcellular locations</h3>
                            <div className="row">
                                <div className="col-md-5 col-12 mb-3">
                                    <CellDiagram highlightedParts={highlightedParts} />
                                </div>
                                <div className="col-md-7 col-12">
                                    <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem' }}>
                                        {subcellularLocations.map((loc, idx) => (
                                            <li key={idx} className="mb-3">
                                                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
                                                    <span>
                                                        {loc.isoform && (
                                                            <strong>[{loc.isoform}]: </strong>
                                                        )}
                                                        {loc.location}
                                                    </span>
                                                    {loc.evidenceBadges.map((badge, bidx) => (
                                                        <EvidenceBadge
                                                            key={bidx}
                                                            label={badge.label}
                                                            eco={badge.eco}
                                                        />
                                                    ))}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ========== CROSS REFERENCES ========== */}
                    {Object.keys(crossReferences).length > 0 && (
                        <div className="row mb-5 col-12">
                            <hr className="mb-4" />
                            <h3 className="fw-bold mb-3">Cross References</h3>
                            <div className="col-12">
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                                        gap: '1rem 2rem',
                                        alignItems: 'start'
                                    }}
                                >
                                    {Object.entries(crossReferences).map(([dbName, entries]) => (
                                        <div
                                            key={dbName}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                gap: '0.75rem'
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontWeight: 'bold',
                                                    minWidth: '110px',
                                                    maxWidth: '110px',
                                                    textAlign: 'right',
                                                    paddingRight: '9rem',
                                                    borderRight: '2px solid #dee2e6',
                                                    flexShrink: 0,
                                                    color: '#495057'
                                                }}
                                            >
                                                {dbName}
                                            </div>
                                            <div
                                                style={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    wordBreak: 'break-word',
                                                    lineHeight: '1.8'
                                                }}
                                            >
                                                {Array.isArray(entries) &&
                                                    entries.map((entry, idx) =>
                                                        renderCrossRefEntry(dbName, entry, idx)
                                                    )
                                                }
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ========== BADGE EVIDENCE ==========
const EvidenceBadge = ({ label, eco }) => (
    <span
        title={`Evidence: ${eco}`}
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            padding: '0.2rem 0.6rem',
            fontSize: '0.72rem',
            fontWeight: '500',
            color: '#fff',
            backgroundColor: '#6c757d',
            borderRadius: '4px',
            border: '1px solid #5a6268',
            whiteSpace: 'nowrap',
            letterSpacing: '0.02em',
            fontFamily: 'system-ui, sans-serif'
        }}
    >
        <span>{label}</span>
        <span style={{ opacity: 0.6 }}>|</span>
        <span style={{ fontFamily: 'monospace' }}>{eco}</span>
    </span>
);

// ========== CELLULA SVG INLINE ==========
// SVG ispirato a SwissBioPics, renderizzato inline (nessun problema iframe/CORS)
const CellDiagram = ({ highlightedParts = [] }) => {
    const isHighlighted = (part) => highlightedParts.includes(part);

    // Colori
    const highlightColor = '#2196f3';
    const highlightFill = 'rgba(33, 150, 243, 0.25)';
    const normalStroke = '#adb5bd';
    const normalFill = '#f8f9fa';

    const getStyle = (part) => ({
        fill: isHighlighted(part) ? highlightFill : normalFill,
        stroke: isHighlighted(part) ? highlightColor : normalStroke,
        strokeWidth: isHighlighted(part) ? 2.5 : 1,
        transition: 'all 0.3s ease',
        cursor: 'default'
    });

    return (
        <div
            style={{
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                padding: '15px',
                backgroundColor: '#e7f0ff',
                minHeight: '360px'
            }}
        >
            <svg
                viewBox="0 0 400 380"
                style={{ width: '100%', height: 'auto', maxHeight: '360px' }}
                xmlns="http://www.w3.org/2000/svg"
            >
                {/* Extracellular space (background) */}
                <rect
                    x="0" y="0" width="400" height="380"
                    style={getStyle('extracellular')}
                    data-part="extracellular"
                >
                    <title>Extracellular space</title>
                </rect>

                {/* Cell membrane + cytoplasm */}
                <ellipse
                    cx="200" cy="190" rx="170" ry="155"
                    style={getStyle('plasma-membrane')}
                    data-part="plasma-membrane"
                >
                    <title>Plasma membrane</title>
                </ellipse>

                {/* Cytoplasm (interno) */}
                <ellipse
                    cx="200" cy="190" rx="160" ry="145"
                    style={{
                        ...getStyle('cytoplasm'),
                        fill: isHighlighted('cytoplasm') || isHighlighted('cytosol')
                            ? highlightFill
                            : '#ffffff'
                    }}
                    data-part="cytoplasm"
                >
                    <title>Cytoplasm / Cytosol</title>
                </ellipse>

                {/* Nucleus */}
                <circle
                    cx="195" cy="180" r="50"
                    style={getStyle('nucleus')}
                    data-part="nucleus"
                >
                    <title>Nucleus</title>
                </circle>
                {/* Nucleolus dentro nucleo */}
                <circle
                    cx="205" cy="175" r="14"
                    fill={isHighlighted('nucleus') ? '#1976d2' : '#ced4da'}
                    opacity="0.6"
                />

                {/* Mitochondrion (ovale sopra) */}
                <ellipse
                    cx="105" cy="115" rx="30" ry="14"
                    transform="rotate(-25 105 115)"
                    style={getStyle('mitochondrion')}
                    data-part="mitochondrion"
                >
                    <title>Mitochondrion</title>
                </ellipse>

                {/* Endoplasmic Reticulum (curve) */}
                <path
                    d="M 260 130 Q 290 150 280 180 Q 270 210 295 230"
                    fill="none"
                    style={{
                        stroke: isHighlighted('er') ? highlightColor : normalStroke,
                        strokeWidth: isHighlighted('er') ? 3 : 2,
                        transition: 'all 0.3s ease'
                    }}
                    data-part="er"
                >
                    <title>Endoplasmic reticulum</title>
                </path>

                {/* Golgi apparatus (pile di curve) */}
                <g data-part="golgi">
                    <title>Golgi apparatus</title>
                    {[0, 1, 2, 3].map(i => (
                        <path
                            key={i}
                            d={`M ${90 + i * 3} ${245 + i * 8} Q ${115} ${240 + i * 8} ${140 - i * 3} ${245 + i * 8}`}
                            fill="none"
                            style={{
                                stroke: isHighlighted('golgi') ? highlightColor : normalStroke,
                                strokeWidth: isHighlighted('golgi') ? 2.5 : 1.5,
                                transition: 'all 0.3s ease'
                            }}
                        />
                    ))}
                </g>

                {/* Lysosome */}
                <circle
                    cx="295" cy="265" r="13"
                    style={getStyle('lysosome')}
                    data-part="lysosome"
                >
                    <title>Lysosome</title>
                </circle>

                {/* Peroxisome */}
                <circle
                    cx="130" cy="290" r="10"
                    style={getStyle('peroxisome')}
                    data-part="peroxisome"
                >
                    <title>Peroxisome</title>
                </circle>

                {/* Centrosome */}
                <g data-part="centrosome">
                    <title>Centrosome</title>
                    <rect
                        x="258" y="95" width="8" height="18"
                        style={getStyle('centrosome')}
                    />
                    <rect
                        x="272" y="100" width="8" height="18"
                        style={getStyle('centrosome')}
                    />
                </g>

                <g data-part="vesicle">
                    <title>Cytoplasmic vesicles / Secretory vesicles</title>
                    {[
                        { cx: 155, cy: 280, r: 7 },
                        { cx: 245, cy: 285, r: 6 },
                        { cx: 80, cy: 200, r: 7 },
                        { cx: 320, cy: 195, r: 8 },
                        { cx: 200, cy: 305, r: 6 }
                    ].map((v, i) => (
                        <circle
                            key={i}
                            cx={v.cx} cy={v.cy} r={v.r}
                            style={getStyle('vesicle')}
                        />
                    ))}
                </g>

                {/* Cell projection (cilium/flagellum) - linea che sporge */}
                <g data-part="cell-projection">
                    <title>Cell projection / Cilium / Flagellum</title>
                    <line
                        x1="200" y1="35" x2="200" y2="5"
                        style={{
                            stroke: isHighlighted('cell-projection') ? highlightColor : normalStroke,
                            strokeWidth: isHighlighted('cell-projection') ? 4 : 2,
                            transition: 'all 0.3s ease'
                        }}
                    />
                    <line
                        x1="195" y1="35" x2="190" y2="8"
                        style={{
                            stroke: isHighlighted('cell-projection') ? highlightColor : normalStroke,
                            strokeWidth: isHighlighted('cell-projection') ? 4 : 2,
                            transition: 'all 0.3s ease'
                        }}
                    />
                    <line
                        x1="205" y1="35" x2="212" y2="10"
                        style={{
                            stroke: isHighlighted('cell-projection') ? highlightColor : normalStroke,
                            strokeWidth: isHighlighted('cell-projection') ? 4 : 2,
                            transition: 'all 0.3s ease'
                        }}
                    />
                </g>

                <text
                    x="200" y="370"
                    textAnchor="middle"
                    fontSize="11"
                    fill="#6c757d"
                    fontFamily="system-ui, sans-serif"
                >
                    {highlightedParts.length > 0
                        ? `${highlightedParts.length} location(s) highlighted`
                        : 'Eukaryotic cell'}
                </text>
            </svg>
        </div>
    );
};

export default OverviewSection;
